import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { AppDispatch } from "../../../../redux/store";
import {
  ChallengeResponse,
  challengesStateAction,
  clearDeleteChallengeState,
  clearNewChallengeState,
  clearUpdateChallengeState,
  deleteChallengeStateAction,
  getChallengesState,
  getCreateChallengeState,
  getDeleteChallengeState,
  getUpdateChallengeState,
} from "../../../../redux/features/other-slice";
import {
  getErrorState,
  setBreadcrumbState,
} from "../../../../redux/features/common-slice";
import paths from "../../../../navigation/paths";
import PermissionGate from "../../../../core/layouts/permission-gate";
import { UserRole } from "../../../../redux/features/user-slice";
import Table from "../../../../components/table";
import Loading from "../../../../components/loading";
import TableBodyCell from "../../../../components/table-body-cell";
import Button from "../../../../components/button";
import ToastComponent from "../../../../components/toast";
import { adminCat, mgTeam } from "../../../../core/utils/constants";
import EmptyDataOverlay from "../../../../components/empty-data-overlay";
import PropertyActions from "../_components/property-actions";
import NewChallengeDialog from "./components/new-challenge-dialog";
import UpdateChallengeDialog from "./components/update-challenge-dialog";

export default function ChallengesPage() {
  const navigate = useNavigate();

  const { id, name } = useParams();

  const dispatch = useDispatch<AppDispatch>();
  const challengesState = useSelector(getChallengesState);
  const newChallengeState = useSelector(getCreateChallengeState);
  const updateChallengeState = useSelector(getUpdateChallengeState);
  const deleteChallengeState = useSelector(getDeleteChallengeState);
  const errorState = useSelector(getErrorState);

  const [openNew, setOpenNew] = useState<boolean>(false);
  const [updateChallenge, setUpdateChallenge] =
    useState<ChallengeResponse | null>(null);
  const [isHover, setIsHover] = useState<{
    [id: string]: boolean;
  }>({});

  const fetchChallenges = useCallback(async () => {
    if (id) {
      await dispatch(challengesStateAction({ categoryId: id as string }));
    }
  }, [dispatch, id]);

  const handleSuccessToast = useCallback(
    (message: string, clearAction: any) => {
      toast(message, { type: "success" });
      setTimeout(() => {
        dispatch(clearAction());
      }, 2000);
    },
    [dispatch]
  );

  const deleteChallenge = async (id: string) => {
    await dispatch(deleteChallengeStateAction({ id: id }));
  };

  // Memoized onMouseEnter handler
  const handleMouseEnter = useCallback((id: string) => {
    setIsHover((prevState) => ({
      ...prevState,
      [id]: true,
    }));
  }, []);

  // Memoized onMouseLeave handler
  const handleMouseLeave = useCallback((id: string) => {
    setIsHover((prevState) => ({
      ...prevState,
      [id]: false,
    }));
  }, []);

  useEffect(() => {
    fetchChallenges();
    dispatch(
      setBreadcrumbState([
        { id: "summaryProperties", path: null, title: "Summary Properties" },
        { id: paths.CHALLENGES, path: null, title: "Challenges" },
        { id: name as string, path: null, title: name as string },
      ])
    );
  }, [fetchChallenges]);

  useEffect(() => {
    if (newChallengeState.data && newChallengeState.data.success) {
      handleSuccessToast(
        newChallengeState.data.message,
        clearNewChallengeState
      );
      fetchChallenges();
    }
  }, [newChallengeState, handleSuccessToast]);

  useEffect(() => {
    if (updateChallengeState.data && updateChallengeState.data.success) {
      handleSuccessToast(
        updateChallengeState.data.message,
        clearUpdateChallengeState
      );
    }
  }, [updateChallengeState, handleSuccessToast]);

  useEffect(() => {
    if (deleteChallengeState.data && deleteChallengeState.data.success) {
      handleSuccessToast(
        deleteChallengeState.data.message,
        clearDeleteChallengeState
      );
    }
  }, [deleteChallengeState, handleSuccessToast]);

  useEffect(() => {
    if (errorState) {
      if (errorState.redirect) {
        navigate(errorState.redirect.destination, {
          replace: errorState.redirect.permanent,
        });
        return;
      }
      toast(errorState.message, { type: "error" });
    }
  }, [errorState]);

  return (
    <PermissionGate
      categories={[adminCat.id]}
      roles={[UserRole.Admin, UserRole.Supervisor]}
    >
      <Helmet title="Challenges" />
      <div className="flex items-center justify-end mb-4">
        <Button
          label="Add Challenge"
          onClick={() => setOpenNew(true)}
          exClassName="truncate"
        />
      </div>
      <Table
        style={{
          maxHeight: "calc(100% - 48px)",
        }}
        headerCells={[
          { id: "title", label: "Title", align: "text-left", width: "w-5/12" },
          {
            id: "description",
            label: "Description",
            align: "text-left",
            width: "w-7/12",
          },
          // {
          //   id: "group",
          //   label: "Group",
          //   align: "text-left",
          //   width: "w-2/12",
          // },
        ]}
        body={
          challengesState.loading
            ? [
                <tr key="loading-row">
                  <td colSpan={2} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <Loading />
                    </div>
                  </td>
                </tr>,
              ]
            : challengesState.data.length > 0
            ? [
                ...challengesState.data.map(
                  (chal: ChallengeResponse, index: number) => {
                    return (
                      <tr
                        key={chal.id}
                        className="border-t border-dashed border-divider hover:bg-action-hover relative"
                        onMouseEnter={() => handleMouseEnter(chal.id)}
                        onMouseLeave={() => handleMouseLeave(chal.id)}
                      >
                        <TableBodyCell exClassName="font-medium whitespace-normal break-words">
                          <label className="cursor-pointer hover:underline">
                            {chal.title}
                          </label>
                        </TableBodyCell>
                        <TableBodyCell exClassName="whitespace-normal break-words text-justify">
                          {chal.description}
                        </TableBodyCell>
                        {/* <TableBodyCell exClassName="w-2/12 whitespace-normal break-words text-justify">
                          {chal.position?.name || "All"}
                        </TableBodyCell> */}
                        {isHover[chal.id] && (
                          <PropertyActions
                            id={chal.id}
                            onUpdate={() => setUpdateChallenge(chal)}
                            onDelete={() => deleteChallenge(chal.id)}
                          />
                        )}
                      </tr>
                    );
                  }
                ),
              ]
            : [
                <tr key="empty-row">
                  <td colSpan={2} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <EmptyDataOverlay
                        label="No Challenges found"
                        exClassName="h-20"
                      />
                    </div>
                  </td>
                </tr>,
              ]
        }
      />
      <ToastComponent id="challenges" />
      <NewChallengeDialog
        categoryId={id as string}
        open={openNew}
        setOpen={setOpenNew}
        onCompleted={fetchChallenges}
      />
      <UpdateChallengeDialog
        challenge={updateChallenge}
        setChallenge={setUpdateChallenge}
        onCompleted={fetchChallenges}
      />
    </PermissionGate>
  );
}
